import React, { useState, useContext, useCallback } from "react";
import { useCategoriesContext } from "domain/Category";
import { useCompaniesContext } from "domain/Company";
import { useSuppliersContext } from "domain/Supplier/context";
import { useAmortizationTypeContext } from "domain/AmortizationType";
import { partChangeLabels, InventoryType } from "shared/constants/constants";
import { InventoryService, NotesService } from "../services";
import { InventoryPartService } from "domain/InventoryPart";
import { InventoryCensusService } from "domain/Census/services/inventory-census.service";
import { HistoryService } from "domain/History";
import { LocationService } from "domain/Location";

const InventoryActionsContext = React.createContext();

export function useInventoryActionsContext() {
  return useContext(InventoryActionsContext);
}

export function InventoryActionsProvider({ children }) {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [changes, setChanges] = useState([]);

  const { categories } = useCategoriesContext();
  const { companies, temporaryOwners } = useCompaniesContext();
  const allCompanyLikeEntities = [...companies, ...temporaryOwners];
  const { suppliers } = useSuppliersContext();
  const { amortizationTypes } = useAmortizationTypeContext();

  const getItem = useCallback(
    async (itemId) => {
      setIsActionLoading(true);
      return InventoryService.get({ itemId })
        .then((data) => {
          if (!data) return null;
          const { companyId, categoryId, ownerId, amortizationTypeId, ...other } = data;
          const company = companies.find((c) => c.id === companyId);
          const owner = allCompanyLikeEntities.find((o) => o.id === ownerId);
          const category = categories.find((c) => c.id === categoryId);
          const amortizationType = amortizationTypes.find((a) => a.id === amortizationTypeId);
          const enrichedParts = (data.parts || []).map((part) => ({
            ...part,
            category: categories.find((c) => c.id === part.categoryId),
            company: companies.find((c) => c.id === part.companyId),
            owner: allCompanyLikeEntities.find((o) => o.id === part.ownerId),
            supplier: suppliers?.find((s) => s.id === part.invoice?.supplierId),
          }));
          return { ...other, company, owner, category, amortizationType, parts: enrichedParts };
        })
        .finally(() => setIsActionLoading(false));
    },
    [amortizationTypes, categories, companies, suppliers]
  );

  const getParentById = useCallback(
    (id) => {
      return InventoryPartService.getParentById(id).then((data) => {
        if (!data) return;

        const { companyId, categoryId, invoiceId, ownerId, ...other } = data;
        const company = companies.find((company) => company.id === companyId);
        const owner = allCompanyLikeEntities.find((owner) => owner.id === ownerId);
        const category = categories.find((category) => category.id === categoryId);

        return {
          ...other,
          company,
          owner,
          category,
        };
      });
    },
    [categories, companies]
  );

  const addInventoryParts = async (inventoryId, items) => {
    return InventoryPartService.addParts({ inventoryId, partIds: items.map((item) => item.id) });
  };

  const deleteInventoryPart = async (inventoryId, partId) => {
    return InventoryPartService.deletePart(inventoryId, partId);
  };

  const deleteInventory = async (inventoryId, archivedReason) => {
    return InventoryService.deleteInventory(inventoryId, archivedReason);
  };

  const changeItemOwnership = async (inventoryId, newOwnerCompany, newPurchasePrice) => {
    setIsActionLoading(true);
    return InventoryService.changeItemOwnership(inventoryId, newOwnerCompany, newPurchasePrice).finally(() =>
      setIsActionLoading(false)
    );
  };

  const validateAllItems = async (inventoryId, items) => {
    const partIds = items.map((part) => part.id.toString());
    InventoryPartService.addParts({ inventoryId, partIds, validateOnly: true }).then((data) => {
      const { parentChanges, partsChanges } = data;
      const changes = [];

      if (parentChanges.stateAfter.totalPrice !== parentChanges.stateBefore.totalPrice) {
        changes.push({
          description: partChangeLabels.get("TOTAL_PRICE"),
          stateBefore: parentChanges.stateBefore.totalPrice,
          stateAfter: parentChanges.stateAfter.totalPrice,
        });
      }

      if (parentChanges.stateAfter.type !== parentChanges.stateBefore.type) {
        changes.push({
          description: partChangeLabels.get("TYPE"),
          stateBefore: InventoryType.SMALL_INVNENTORY,
          stateAfter: InventoryType.FIXED_ASSET,
        });
      }

      if (partsChanges.stateAfter[0].type !== partsChanges.stateBefore[0].type) {
        changes.push({
          description: partChangeLabels.get("TYPE"),
          stateBefore: InventoryType.SMALL_INVNENTORY,
          stateAfter: InventoryType.FIXED_ASSET,
        });
      }

      if (partsChanges.stateAfter[0].assignedTo !== partsChanges.stateBefore[0].assignedTo) {
        changes.push({
          description: partChangeLabels.get("ASSIGNED_TO"),
          stateBefore: partsChanges.stateBefore[0].assignedTo,
          stateAfter: partsChanges.stateAfter[0].assignedTo
            ? partsChanges.stateAfter[0].assignedTo
            : "slobodan inventar",
        });
      }
      setChanges(changes);
    });
  };

  const serialNumberAlreadyExist = async (serialNumber) => {
    const params = { serialNumber: serialNumber };
    return InventoryService.get(params).then((response) => response.length === 0);
  };

  const getAllItems = useCallback(async (items) => Promise.all(items.map((item) => getItem(item.itemId))), [getItem]);
  const getIsItemSmallInventory = useCallback(
    async (item) => InventoryService.getIsSmallInventory(item).then(({ isSmallInventory }) => isSmallInventory),
    []
  );
  const addItemToCensus = async (item) => InventoryCensusService.addItem(item);
  const getHistoryById = async (id, params) => HistoryService.getById(id, params);

  const getNotesById = (id) => {
    return NotesService.getByInventoryId(id).then((data) => data);
  };

  const createNote = async (inventoryId, text) => {
    try {
      const created = await NotesService.create({ inventoryId, text });

      return created;
    } catch (error) {
      throw error;
    }
  };

  const getWorkspaceLocations = async (params) => LocationService.getWorkspaceLocations(params);

  const updateItem = async (item) => {
    return InventoryService.update(item).then((inventory) => {
      return inventory.updatedInventory;
    });
  };

  const getParts = useCallback(
    async (inventoryId, searchTerm, page = 1, pageSize = 100) => {
      const params = { isPart: true, exclude: inventoryId, searchTerm: searchTerm, page, pageSize };
      return InventoryService.get(params).then((response) => {
        const parts = response?.data?.map((part) => {
          const company = companies.find((company) => company.id === part.companyId);
          const owner = allCompanyLikeEntities.find((owner) => owner.id === part.ownerId);
          const category = categories.find((category) => category.id === part.categoryId);
          return {
            itemId: part.itemId,
            name: part.label,
            serialNumber: part.serialNumber,
            navigatorId: part.navigatorId,
            value: part.id,
            company,
            owner,
            category,
          };
        });
        return { data: parts, total: response?.total };
      });
    },
    [categories, companies]
  );

  const value = {
    isActionLoading,
    getItem,
    getParentById,
    addInventoryParts,
    deleteInventoryPart,
    deleteInventory,
    changeItemOwnership,
    validateAllItems,
    serialNumberAlreadyExist,
    getAllItems,
    getIsItemSmallInventory,
    addItemToCensus,
    getHistoryById,
    getWorkspaceLocations,
    changes,
    updateItem,
    getParts,
    getNotesById,
    createNote,
  };

  return <InventoryActionsContext.Provider value={value}>{children}</InventoryActionsContext.Provider>;
}
